import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { useFormularioSecretariaMunicipalAgriculturaStore } from 'stores/forms/formularioAgricultura.store';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';


const chipEstiloNaoInformado = {
  backgroundColor: '#e0e0e0',
  fontSize: '0.75rem',
  color: '#424242',
  fontWeight: 500,
};

const InfoField: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <Box mb={2}>
    <Typography
      sx={{
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "143%",
        letterSpacing: "0.17px",
        color: "text.secondary",
        mb: 0.5,
      }}
      gutterBottom
    >
      {label}
    </Typography>
    {value ? (
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "24px",
          letterSpacing: "0.15px",
          color: "text.primary",
        }}
      >
        {value}
      </Typography>
    ) : (
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "24px",
          letterSpacing: "0.15px",
          color: "text.primary",
        }}
      >
        Não informado
      </Typography>
    )}
  </Box>
);

const ArrayField: React.FC<{ label: string; value: string[] | null }> = ({ label, value }) => (
  <Box mb={2}>
    <Typography
      sx={{
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "143%",
        letterSpacing: "0.17px",
        color: "text.secondary",
        mb: 0.5,
      }}
      gutterBottom
    >
      {label}
    </Typography>
    {value && value.length > 0 ? (
      <List dense>
        {value.map((item, index) => (
          <ListItem key={index} sx={{ pl: 0 }}>
            <ListItemText primary={`• ${item}`} />
          </ListItem>
        ))}
      </List>
    ) : (
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "24px",
          letterSpacing: "0.15px",
          color: "text.primary",
        }}
      >
        Não informado
      </Typography>
    )}
  </Box>
);

const Step3View: React.FC = () => {
  const { formulario } = useFormularioSecretariaMunicipalAgriculturaStore();
  const [open, setOpen] = useState<boolean>(true);

  if (!formulario) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography>Carregando informações...</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 0, mt: 2 }}>
      <Box
        sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(!open)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: "#000000DE",
              fontSize: "20px",
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {open ? (
              <KeyboardArrowUpOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
            ) : (
              <KeyboardArrowDownOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
            )}
            Órgãos Estaduais
          </Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />
      </Box>

      {open && (
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: '18px',
                  color: "#000000DE",
                  lineHeight: '160%',
                  letterSpacing: '0.15px',
                }}
              >
                EMPAER (Empresa Mato-grossense de Pesquisa, Assistência e Extensão Rural)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Possui Escritório da Empaer"
                value={formulario.possui_escritorio_empaer ? 'Sim' : 'Não'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Quantidade de Escritórios"
                value={formulario.quantidade_escritorios}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Possui Termo de Cooperação com a Empaer"
                value={formulario.desenvolve_projetos_empaer ? 'Sim' : 'Não'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Nome do Projeto EMPAER"
                value={formulario.projeto_empaer_nome}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Possui Servidores Cedidos para a EMPAER"
                value={formulario.servidores_cedidos_empaer ? 'Sim' : 'Não'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ArrayField
                label="Nomes dos Servidores Cedidos para EMPAER"
                value={formulario.nome_servidores_cedidos_empaer}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: '18px',
                  color: "#000000DE",
                  lineHeight: '160%',
                  letterSpacing: '0.15px',
                }}
              >
                INDEA (Instituto de Defesa Agropecuária de Mato Grosso)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Possui Escritório do Indea"
                value={formulario.possui_escritorio_indea ? 'Sim' : 'Não'}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: '18px',
                  color: "#000000DE",
                  lineHeight: '160%',
                  letterSpacing: '0.15px',
                }}
              >
                SEMA (Secretaria de Estado de Meio Ambiente)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="Possui Descentralização Ambiental"
                value={formulario.possui_unidade_sema ? 'Sim' : 'Não'}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: '18px',
                  color: "#000000DE",
                  lineHeight: '160%',
                  letterSpacing: '0.15px',
                }}
              >
                Cadastro Nacional da Agricultura Familiar (CAF)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField
                label="O município é emissor de Cadastro Nacional da Agricultura Familiar (CAF)"
                value={formulario.emissor_caf ? 'Sim' : 'Não'}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default Step3View;
